import { DatabaseIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { TraceGridRow } from "./types";
import { useRecentlyViewedTraceSession } from "./useRecentlyViewedTraceSession";

import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import { useStructuredSearchGridPaginationSort } from "@/components/StructuredSearch/hooks/useStructuredSearchGridPaginationSort";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { useUser } from "@/context/user-context";

export const useTracesGridInteractions = (onAddToDataset?: (row: TraceGridRow) => void) => {
  const structuredSearchStore = useStructuredSearchStore();
  const workspaceId = useUser()?.activeWorkspaceId ?? "";
  const navigate = useNavigate();
  const location = useLocation();
  const { recentlyViewedTraceId, markRecentlyViewedTrace } = useRecentlyViewedTraceSession();

  const buildTraceNavigationSearch = useCallback((): string => {
    const params = applyStructuredSearchStateToSearchParams(
      new URLSearchParams(location.search),
      {
        filters: structuredSearchStore.filters,
        dateRange: structuredSearchStore.dateRange,
        q: structuredSearchStore.q,
        pageIndex: structuredSearchStore.pageIndex,
        pageSize: structuredSearchStore.pageSize,
        sorting: structuredSearchStore.sorting,
      },
      { namespace: "tracesSidebar" },
    );
    const next = params.toString();
    return next ? `?${next}` : "";
  }, [
    location.search,
    structuredSearchStore.dateRange,
    structuredSearchStore.filters,
    structuredSearchStore.pageIndex,
    structuredSearchStore.pageSize,
    structuredSearchStore.q,
    structuredSearchStore.sorting,
  ]);

  const tracePath = useCallback(
    (traceId: string) =>
      `/workspace/${workspaceId}/traces/${traceId}${buildTraceNavigationSearch()}`,
    [buildTraceNavigationSearch, workspaceId],
  );

  const openTraceInNewTab = useCallback(
    (traceId: string) => {
      markRecentlyViewedTrace(traceId);
      window.open(tracePath(traceId), "_blank", "noopener,noreferrer");
    },
    [markRecentlyViewedTrace, tracePath],
  );

  const handleRowClick = useCallback(
    (row: TraceGridRow, event?: MouseEvent) => {
      if (!row?.trace_id) {
        return;
      }

      // Clicks anywhere in the select column belong to the checkbox UI, not
      // navigation — including the cell's padding area around the checkbox.
      const target = event?.target as HTMLElement | null | undefined;
      const cellColId = target?.closest("[col-id]")?.getAttribute("col-id");
      if (cellColId === "col_-1") {
        return;
      }

      // Shift is reserved for range-select interactions; never navigate.
      if (event?.shiftKey) {
        return;
      }

      if (event?.metaKey || event?.ctrlKey) {
        openTraceInNewTab(row.trace_id);
        return;
      }
      markRecentlyViewedTrace(row.trace_id);
      navigate(tracePath(row.trace_id));
    },
    [markRecentlyViewedTrace, navigate, openTraceInNewTab, tracePath],
  );

  const { currentPage, pageSize, handlePaginationChanged, handleSortChanged } =
    useStructuredSearchGridPaginationSort();

  const contextMenuItems = useMemo(
    () => [
      {
        id: "open-in-new-tab",
        label: "Open in new tab",
        icon: ExternalLinkIcon,
        onClick: (row?: TraceGridRow) => {
          if (!row?.trace_id) {
            return;
          }
          openTraceInNewTab(row.trace_id);
        },
      },
      {
        id: "divider-open-in-new-tab",
        isDivider: true,
        showIf: (row: TraceGridRow) => Boolean(row?.trace_id),
      },
      {
        id: "add-to-dataset",
        label: "Add to dataset",
        icon: DatabaseIcon,
        showIf: (row: TraceGridRow) => Boolean(row?.trace_id),
        onClick: (row?: TraceGridRow) => {
          if (!row?.trace_id) {
            return;
          }
          onAddToDataset?.(row);
        },
      },
    ],
    [onAddToDataset, openTraceInNewTab],
  );

  return {
    contextMenuItems,
    currentPage,
    handlePaginationChanged,
    handleRowClick,
    handleSortChanged,
    pageSize,
    recentlyViewedTraceId,
  };
};
